/**
 * Reading an unstructured mail body with the model, and REVIEWING the result before it becomes
 * an enquiry.
 *
 * ---- Why there is a review step at all ----
 *
 * Every other path into an enquiry reads a document that carries its own structure — a
 * spreadsheet's column headings, a pasted grid, a numbered list with units. Those are parsed
 * deterministically and go straight in. This path reads PROSE, where there is no structure to
 * check the answer against, so the model's reading is a proposal and nothing more.
 *
 * The grid is editable for the two things a reader gets wrong: a quantity the mail never stated
 * (which arrives as blank, deliberately — `mailBodyExtract.ts` refuses to guess one), and a line
 * that is not an order at all. Removing a line is one click; adding one the model missed is the
 * same manual entry the purchaser would otherwise have done for the whole mail.
 *
 * Nothing here writes. `onConfirm` hands the reviewed lines back to the caller, which creates the
 * enquiry through the same path every other source uses.
 */

import { useState } from 'react'
import { Modal } from '../../components/Modal'
import { Button, Placeholder } from '../../components/ui'
import type { SheetItem } from '../../lib/sheetExtract'

export function ReadBodyModal({
  lines,
  pending,
  error,
  onRetry,
  onConfirm,
  onClose,
}: {
  lines: SheetItem[] | null
  pending: boolean
  error: string | null
  onRetry: () => void
  onConfirm: (lines: SheetItem[]) => void
  onClose: () => void
}) {
  /** `null` until the read lands, then the purchaser's working copy. */
  const [draft, setDraft] = useState<SheetItem[] | null>(null)
  const rows = draft ?? lines ?? []

  const edit = (index: number, patch: Partial<SheetItem>) =>
    setDraft(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  const remove = (index: number) => setDraft(rows.filter((_, i) => i !== index))
  // A hand-added line carries no customer code or serial — see `mailBodyExtract`'s note.
  const add = () => setDraft([...rows, { description: '', qty: 0, uom: '', item_code: null, row_no: null }])

  // A line with no description cannot be matched, priced, or asked about — it is not a line.
  const usable = rows.filter((r) => r.description.trim() !== '')
  const missingQty = usable.filter((r) => !r.qty).length

  return (
    <Modal
      title="Read the items from this mail"
      onClose={onClose}
      footer={
        <>
          <Button onClick={add} disabled={pending}>
            ✚ Add a line
          </Button>
          <span style={{ flex: 1 }} />
          <Button variant="primary" disabled={pending || usable.length === 0} onClick={() => onConfirm(usable)}>
            Use these {usable.length} line{usable.length === 1 ? '' : 's'}
          </Button>
        </>
      }
    >
      <div className="pad" style={{ color: 'var(--muted)', fontSize: 12.5 }}>
        This mail carries no table or numbered list, so the text was read by the assistant. Check
        it before it becomes an enquiry — <b>a quantity the mail never stated is left blank rather
        than guessed</b>, and anything that is not an order should be removed.
      </div>

      {pending ? <Placeholder>Reading the mail…</Placeholder> : null}

      {error ? (
        <div className="pad">
          <div className="warnflag" style={{ marginBottom: 8 }}>
            {error}
          </div>
          <Button onClick={onRetry}>↻ Try again</Button>
        </div>
      ) : null}

      {!pending && !error && rows.length === 0 ? (
        <div className="pad" style={{ color: 'var(--muted)' }}>
          Nothing in this mail reads as an order. Add the lines by hand, or close and enter them on
          the RFQ page.
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th className="right" style={{ width: 90 }}>Qty</th>
                <th style={{ width: 110 }}>UOM</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td>
                    <input
                      value={row.description}
                      style={{ width: '100%' }}
                      onChange={(e) => edit(index, { description: e.target.value })}
                    />
                  </td>
                  <td>
                    {/* Blank, not 0 — the mail did not say, and a zero in the box invites someone
                        to accept it as the answer. */}
                    <input
                      value={row.qty || ''}
                      placeholder="—"
                      inputMode="decimal"
                      style={{ width: '100%', textAlign: 'right' }}
                      onChange={(e) => edit(index, { qty: Number(e.target.value) || 0 })}
                    />
                  </td>
                  <td>
                    <input
                      value={row.uom ?? ''}
                      placeholder="—"
                      style={{ width: '100%' }}
                      onChange={(e) => edit(index, { uom: e.target.value })}
                    />
                  </td>
                  <td>
                    <button className="linkish" title="Remove this line" onClick={() => remove(index)}>
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Named rather than blocking: a line whose quantity the customer never stated is a real
          line, and the purchaser confirms it with the vessel — not with this dialog. */}
      {missingQty > 0 ? (
        <div className="pad" style={{ color: 'var(--warn)', fontSize: 12 }}>
          ⚠ {missingQty} line{missingQty === 1 ? '' : 's'} have no quantity — the mail did not state
          one. Fill them in, or leave them for the RFQ page.
        </div>
      ) : null}
    </Modal>
  )
}
